import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bus, User, Phone, ArrowLeft, MapPin, CheckCheck } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const DROPOFF_PREF = { collected_stop: "🚏 Collect from nearest bus stop", walk_home: "🚶 May walk home from bus stop", door: "🏠 Bus can reach the door" };

export default function StaffTransport() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState("2026-10-05");
  const [data, setData] = useState({ pickups_by_zone: [], dropoffs: [], dropoffs_by_school: [] });
  const [bulk, setBulk] = useState(null);

  const load = useCallback(() => {
    api.get(`/staff/transport?date=${date}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.post(`/staff/transport/${id}/status`, { status }); toast.success("Status updated · parent notified"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const runBulk = async () => {
    if (!bulk) return;
    const url = bulk.kind === "released" ? "/staff/transport/bulk-released" : "/staff/transport/bulk-arrived";
    const verb = bulk.kind === "released" ? "released" : "checked in";
    try {
      const { data: r } = await api.post(url, { date, school: bulk.school });
      toast.success(`${r.count} explorer${r.count === 1 ? "" : "s"} ${verb} from ${bulk.school}`);
      setBulk(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const ChildCard = ({ b, buttons }) => (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex gap-3" data-testid={`transport-child-${b.id}`}>
      <div className="w-16 h-16 rounded-xl bg-turquoise/20 flex items-center justify-center overflow-hidden shrink-0">
        {b.photo_url ? <img src={b.photo_url} className="w-full h-full object-cover" alt="" /> : <User className="w-8 h-8 text-turquoise" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold text-white">{b.child_name}</p>
        <p className="font-mono text-turquoise text-xs">{b.explorer_id}</p>
        {b.allergies && <p className="text-[11px] text-coral">⚠ {b.allergies}</p>}
        {b.transport_only && <span className="inline-block text-[10px] font-bold bg-gold text-navy rounded-full px-2 py-0.5 mt-0.5">TRANSPORT ONLY</span>}
        {b.info?.pickup_location && <p className="text-[11px] text-white/60"><MapPin className="w-3 h-3 inline" /> Pickup: {b.info.pickup_location}</p>}
        {b.info?.dismissal_time && <p className="text-[11px] text-white/50">Dismissal: {b.info.dismissal_time}</p>}
        {b.info?.pickup_notes && <p className="text-[11px] text-white/50">Note: {b.info.pickup_notes}</p>}
        {b.info?.dropoff_address && <p className="text-[11px] text-white/60"><MapPin className="w-3 h-3 inline" /> {b.info.dropoff_address}</p>}
        {b.info?.dropoff_preference && <p className="text-[11px] text-turquoise font-semibold">{DROPOFF_PREF[b.info.dropoff_preference] || b.info.dropoff_preference}</p>}
        {b.info?.receiving_person && <p className="text-[11px] text-white/60">To: {b.info.receiving_person} ({b.info.receiving_phone})</p>}
        <p className="text-[11px] text-white/50 flex items-center gap-1"><Phone className="w-3 h-3" /> {b.parent_phone || "—"}</p>
        <span className="inline-block mt-1 text-[10px] font-bold text-gold bg-gold/15 rounded-full px-2 py-0.5">{(b.transport_status || "requested").replace("_", " ")}</span>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {buttons.map(([label, st]) => (
            <button key={st} onClick={() => setStatus(b.id, st)} data-testid={`tstatus-${b.id}-${st}`} className="text-[11px] font-semibold bg-turquoise/20 hover:bg-turquoise text-turquoise hover:text-navy rounded-full px-3 py-1 transition-colors">{label}</button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-navy text-white">
      <header className="h-16 flex items-center justify-between gap-2 px-4 border-b border-white/10">
        <div className="flex items-center gap-2 shrink-0"><img src={LOGO} className="h-9 w-9 object-contain" alt="logo" /><p className="font-display font-bold text-sm hidden sm:block">TRANSPORT DASHBOARD</p></div>
        <div className="flex items-center gap-1 overflow-x-auto [&>button]:shrink-0">
          <Button variant="ghost" size="sm" onClick={() => nav("/staff")} className="text-white/70">Scanner</Button>
          <Button variant="ghost" size="sm" onClick={() => nav("/staff/attendance")} className="text-white/70" data-testid="goto-register-btn">Register</Button>
          <Button variant="ghost" size="sm" onClick={() => nav("/staff/camp-roster")} className="text-white/70" data-testid="goto-camp-roster-btn">Camp Roster</Button>
          <Button variant="ghost" size="sm" onClick={() => nav("/staff/dinners")} className="text-white/70" data-testid="goto-dinners-btn">Dinners</Button>
          <Button variant="ghost" size="sm" onClick={() => nav(["admin", "manager"].includes(user?.role) ? "/admin" : "/dashboard")} className="text-white/70"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }}>✕</Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-5">
        <div className="flex items-center gap-2 mb-5">
          <Bus className="w-6 h-6 text-turquoise" />
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl bg-white/10 border-white/20 text-white w-44" data-testid="transport-date" />
        </div>

        <h2 className="font-display font-bold text-gold text-sm tracking-widest uppercase mb-3">Today's School Pickups — by Route Zone</h2>
        {(data.pickups_by_zone || []).length === 0 && <p className="text-white/40 text-sm mb-6">No pickups scheduled.</p>}
        {(data.pickups_by_zone || []).map((zone) => (
          <div key={zone.parish} className="mb-7" data-testid={`pickup-zone-${zone.parish}`}>
            <div className="flex items-center gap-2 mb-2 border-b border-white/10 pb-1">
              <MapPin className="w-4 h-4 text-turquoise" />
              <p className="font-display font-extrabold text-turquoise text-sm tracking-wide uppercase">{zone.parish}</p>
              <span className="text-white/40 text-xs">· {zone.count} {zone.count === 1 ? "kid" : "kids"}</span>
            </div>
            {zone.schools.map((grp) => (
              <div key={grp.school} className="mb-5 pl-1" data-testid={`pickup-school-${grp.school}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="font-display font-extrabold text-lg text-white">{grp.count} {grp.count === 1 ? "kid" : "kids"} · <span className="text-turquoise">{grp.school}</span>{grp.dismissal_time && <span className="text-gold text-xs font-semibold ml-2">🕒 {grp.dismissal_time}</span>}</p>
                  <button onClick={() => setBulk({ ...grp, kind: "arrived" })} data-testid={`bulk-arrived-${grp.school}`} className="text-[11px] font-bold bg-turquoise hover:bg-turquoise/80 text-navy rounded-full px-3 py-1.5 transition-colors flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" /> Arrived at Rock — whole group</button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                  {grp.children.map((b) => <ChildCard key={b.id} b={b} buttons={[["Collected", "collected"], ["Absent", "absent"], ["Arrived at Rock", "arrived"]]} />)}
                </div>
              </div>
            ))}
          </div>
        ))}

        <h2 className="font-display font-bold text-gold text-sm tracking-widest uppercase mb-3 mt-8">Today's Drop-Offs</h2>
        {data.dropoffs_by_school.length === 0 && <p className="text-white/40 text-sm">No drop-offs scheduled.</p>}
        {data.dropoffs_by_school.map((grp) => (
          <div key={grp.school} className="mb-6" data-testid={`dropoff-school-${grp.school}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="font-display font-extrabold text-lg text-white">{grp.count} {grp.count === 1 ? "kid" : "kids"} · <span className="text-gold">{grp.school}</span></p>
              <button onClick={() => setBulk({ ...grp, kind: "released" })} data-testid={`bulk-released-${grp.school}`} className="text-[11px] font-bold bg-gold hover:bg-gold/80 text-navy rounded-full px-3 py-1.5 transition-colors flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" /> Released — whole group</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              {grp.children.map((b) => <ChildCard key={b.id} b={b} buttons={[["Left Rock", "dropped"], ["Released", "released"]]} />)}
            </div>
          </div>
        ))}
      </main>

      <AlertDialog open={!!bulk} onOpenChange={(o) => !o && setBulk(null)}>
        <AlertDialogContent data-testid="bulk-arrived-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{bulk?.kind === "released" ? "Release the whole group?" : "Check in the whole group?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {bulk?.kind === "released"
                ? <>This marks all {bulk?.count} explorer{bulk?.count === 1 ? "" : "s"} from <b>{bulk?.school}</b> as released to their authorized adult and checked out on the register. Parents will be notified.</>
                : <>This marks all {bulk?.count} explorer{bulk?.count === 1 ? "" : "s"} from <b>{bulk?.school}</b> as arrived at Adventure Rock and checked in on the register. Parents will be notified.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runBulk} data-testid="bulk-confirm" className="bg-turquoise text-navy font-bold">{bulk?.kind === "released" ? "Confirm release" : "Confirm arrival"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
